import { z } from 'zod';
const count = z.number().int().nonnegative();
const unreadableFields = {
    policy: z.literal('held-unusable-v1'),
    status: z.literal('partial'),
    sourceUnitCount: z.number().int().positive().safe(),
    recoveredUnitCount: z.number().int().nonnegative().safe(),
    heldUnitCount: z.number().int().positive().safe(),
    opaqueRegionCount: z.literal(0),
    opaqueTokenCount: z.number().int().nonnegative().safe(),
    metricScope: z.literal('recovered_units_partial'),
};
const unreadableAccounts = (value) => value.sourceUnitCount === value.recoveredUnitCount + value.heldUnitCount;
const unreadableFileRecoveryCoverageSchema = z.object(unreadableFields).strict()
    .refine(unreadableAccounts, 'Unreadable-statement coverage must account for every source unit.');
const unreadableSummarySchema = z.object({
    policy: z.literal('held-unusable-v1'),
    partialFiles: z.number().int().positive().safe(),
    sourceUnitCount: z.number().int().positive().safe(),
    recoveredUnitCount: z.number().int().nonnegative().safe(),
    heldUnitCount: z.number().int().positive().safe(),
}).strict().refine(value => unreadableAccounts(value) && value.partialFiles <= value.heldUnitCount, 'Unreadable-statement coverage must account for every partial file and source unit.');
const unreadableRecoveryCoverageSchema = z.object({ ...unreadableFields,
    partialFiles: z.number().int().positive().safe(),
}).strict().refine(value => unreadableAccounts(value) && value.partialFiles <= value.heldUnitCount, 'Unreadable-statement coverage must account for every partial file and source unit.');
const recoveryFields = {
    policy: z.literal('dynamic-opaque-v1'),
    status: z.literal('partial'),
    sourceUnitCount: z.number().int().positive().safe(),
    recoveredUnitCount: z.number().int().nonnegative().safe(),
    heldUnitCount: z.number().int().nonnegative().safe(),
    opaqueRegionCount: z.number().int().positive().safe(),
    opaqueTokenCount: z.number().int().positive().safe(),
    metricScope: z.literal('original_sqlglot_partial'),
    unreadableUnitCount: z.number().int().positive().safe().optional(),
};
function consistentRecovery(value) {
    return value.sourceUnitCount === value.recoveredUnitCount + value.heldUnitCount
        && value.opaqueTokenCount >= value.opaqueRegionCount
        && (value.unreadableUnitCount ?? 0) <= value.heldUnitCount;
}
const dynamicFileRecoveryCoverageSchema = z.object(recoveryFields).strict()
    .refine(consistentRecovery, 'Recovery counts must account for every source unit and opaque region.');
const dynamicRecoveryCoverageSchema = z.object({ ...recoveryFields,
    partialFiles: z.number().int().positive().safe(),
    unreadable: unreadableSummarySchema.optional(),
}).strict().refine(value => consistentRecovery(value) && value.partialFiles <= value.opaqueRegionCount, 'Recovery counts must account for every partial file, source unit and opaque region.');
const insertExecFields = {
    ...recoveryFields,
    policy: z.enum(['insert-exec-call-v1', 'insert-exec-call-v2']),
    recoveredUnitCount: z.number().int().nonnegative().safe(),
    partiallyRecoveredUnitCount: z.number().int().positive().safe(),
    opaqueRegionCount: z.number().int().nonnegative().safe(),
    opaqueTokenCount: z.number().int().nonnegative().safe(),
    dynamicPolicy: z.literal('dynamic-opaque-v1').optional(),
};
const insertExecCoverageObject = z.object(insertExecFields).strict();
function consistentInsertExec(value) {
    return value.sourceUnitCount === value.recoveredUnitCount + value.partiallyRecoveredUnitCount + value.heldUnitCount
        && value.opaqueTokenCount >= value.opaqueRegionCount
        && (value.unreadableUnitCount ?? 0) <= value.heldUnitCount
        && (value.opaqueRegionCount > 0
            ? value.dynamicPolicy === 'dynamic-opaque-v1'
            : !Object.prototype.hasOwnProperty.call(value, 'dynamicPolicy') && value.opaqueTokenCount === 0
                && value.heldUnitCount === (value.unreadableUnitCount ?? 0));
}
const insertExecFileRecoveryCoverageSchema = insertExecCoverageObject.refine(consistentInsertExec, 'Partial call coverage must account for every original owner and actual dynamic gap.');
const insertExecRecoveryCoverageSchema = z.object({ ...insertExecFields,
    partialFiles: z.number().int().positive().safe(),
    unreadable: unreadableSummarySchema.optional(),
}).strict().refine(value => consistentInsertExec(value)
    && value.partialFiles <= value.partiallyRecoveredUnitCount + value.opaqueRegionCount, 'Partial call coverage must account for every partial file and original owner.');
export const sourceFileRecoveryCoverageSchema = z.union([
    dynamicFileRecoveryCoverageSchema, insertExecFileRecoveryCoverageSchema, unreadableFileRecoveryCoverageSchema,
]);
export const sourceRecoveryCoverageSchema = z.union([
    dynamicRecoveryCoverageSchema, insertExecRecoveryCoverageSchema, unreadableRecoveryCoverageSchema,
]);
export function unreadableStatementsNotice(coverage) {
    const statements = `${coverage.heldUnitCount} statement${coverage.heldUnitCount === 1 ? '' : 's'}`;
    const files = coverage.partialFiles === undefined ? ''
        : ` in ${coverage.partialFiles} file${coverage.partialFiles === 1 ? '' : 's'}`;
    return `${statements}${files} could not be read by the SQL parser and are listed as gaps. `
        + 'The other statements were analyzed; objects or relationships defined only by the unread statements are missing.';
}
export function insertExecRecoveryNotice(coverage) {
    return 'The procedure call was recorded, but this INSERT EXEC statement was not fully analyzed. '
        + 'The destination is retained where the original analysis identified it; other effects and result columns remain unknown.'
        + (coverage.opaqueRegionCount > 0
            ? ' SQL built or selected at runtime also remains unknown; verified static results do not establish complete behavior or dependencies.' : '');
}
export const workloadCoverageStatusSchema = z.enum([
    'analyzed', 'partial', 'unsupported', 'needsClarification', 'notAnalyzed',
]);
const counts = z.object({
    analyzed: count,
    partial: count,
    unsupported: count,
    needsClarification: count,
    notAnalyzed: count,
});
const file = z.object({
    sourceFileId: z.string(),
    repositoryId: z.string(),
    path: z.string(),
    kind: z.enum(['sql', 'notebook', 'pipeline', 'other']),
    format: z.string(),
    status: workloadCoverageStatusSchema,
    reason: z.string(),
    nextAction: z.string().optional(),
    evaluator: z.object({ id: z.string(), version: z.number().int().positive() }).nullable(),
    analysis: z.object({
        status: z.string(),
        parserVersion: z.string().nullable(),
        sourceInputDigest: z.string().nullable(),
        freshness: z.enum(['current', 'stale', 'unavailable', 'notChecked']),
        parseConfidence: z.string().nullable(),
        parseQualityScore: z.number().nullable(),
        recoveryCoverage: sourceFileRecoveryCoverageSchema.optional(),
    }),
    relatedSourceFileIds: z.array(z.string()),
});
export const workloadCoverageSchema = z.object({
    version: z.literal(1),
    state: z.enum(['ready', 'unavailable']),
    scope: z.literal('retained_source_files'),
    sourceRevision: z.string().nullable(),
    totalFiles: count.nullable(),
    counts: counts.nullable(),
    files: z.array(file),
    notices: z.array(z.string()),
    unavailableReason: z.string().optional(),
}).superRefine((value, context) => {
    if (value.state !== 'ready') {
        if (value.counts !== null || value.files.length > 0) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Unavailable coverage cannot report measured file counts.' });
        }
        return;
    }
    const unique = new Set(value.files.map(entry => entry.sourceFileId));
    if (!value.sourceRevision || !value.counts || value.totalFiles !== value.files.length || unique.size !== value.files.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'Ready coverage must account for every retained file exactly once.' });
        return;
    }
    for (const status of workloadCoverageStatusSchema.options) {
        if (value.counts[status] !== value.files.filter(entry => entry.status === status).length) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: 'Coverage counts must match the file evidence.' });
        }
    }
});
export function isWorkloadSourceReady(source) {
    return source.status === 'READY' || source.status === 'PARTIAL_READY';
}
export const SOURCE_FAILED_FILES_LIMIT = 20;
const CAUSE_CODE = /^[a-z][a-z0-9_]{0,95}$/;
const code = (value) => (typeof value === 'string' && CAUSE_CODE.test(value) ? value : null);
export function readSourceFailedFiles(value, failed) {
    if (!Array.isArray(value) || value.length > Math.min(failed, SOURCE_FAILED_FILES_LIMIT))
        return undefined;
    const files = [];
    for (const item of value) {
        if (!item || typeof item !== 'object')
            return undefined;
        const { name, stage, code: primary, reasonCode } = item;
        if (typeof name !== 'string' || !name.trim() || name.length > 512)
            return undefined;
        files.push({ name, stage: code(stage), code: code(primary), reasonCode: code(reasonCode) });
    }
    return files;
}
export const COVERAGE_NOTICES = [
    'Counts cover retained source files, including any extracted SQL fragments. They are not object counts or a migration success rate.',
    'SQL structural analysis does not certify engine-specific behavior, conversion, execution, scheduling or external dependencies.',
    'Files discarded by an uploader are outside this retained-file catalog. Uncertain formats require review; recognition alone never establishes support.',
];
export function unavailableWorkloadCoverage(reason, totalFiles = null) {
    return {
        version: 1, state: 'unavailable', scope: 'retained_source_files',
        sourceRevision: null, totalFiles, counts: null, files: [],
        notices: [...COVERAGE_NOTICES], unavailableReason: reason,
    };
}
export function normalizeWorkloadCoverage(value) {
    if (value === undefined)
        return undefined;
    const result = workloadCoverageSchema.safeParse(value);
    return result.success ? result.data : unavailableWorkloadCoverage('The saved file coverage evidence could not be read. Refresh the assessment before relying on file coverage.');
}
//# sourceMappingURL=workloadCoverage.js.map